import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfigurationModel } from '../../../../../shared/models/configuration.model';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { MapLayerConfiguration, MapLayerSource, MapLayerType } from '../../../../../shared/models/map-layer.model';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatListItem, MatListItemMeta, MatNavList } from '@angular/material/list';
import { ConfirmationDialogComponent } from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { BaseMapType, SettingsService } from '../../../../../configuration/settings.service';
import { CardComponent } from '../../../../../shared/components/card/card.component';
import { CardContentDirective, CardContentRawDirective } from '../../../../../shared/components/card/card-content.directive';
import { ReactiveFormsModule } from '@angular/forms';
import { MapLayerEditorComponent } from '../map-layer-editor/map-layer-editor.component';

@Component({
  selector: 'app-custom-layers-config',
  templateUrl: './custom-layers-config.component.html',
  styleUrls: ['./custom-layers-config.component.scss', '../../../settings-common.scss'],
  imports: [
    MatProgressSpinner,
    MatIcon,
    MatMenuTrigger,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatButton,
    MatTooltip,
    MatNavList,
    MatListItem,
    MatListItemMeta,
    MatMenuContent,
    CardComponent,
    CardContentDirective,
    ReactiveFormsModule,
    CardContentRawDirective,
    MapLayerEditorComponent,
  ],
})
export class CustomLayersConfigComponent implements OnInit, OnDestroy {

  @Output() editing = new EventEmitter<boolean>();

  configuration: ConfigurationModel;
  mapLayers: MapLayerConfiguration[];

  mapLayerIndex = -1;
  editedMapLayer: MapLayerConfiguration;

  editingStateValid = false;
  mapLayerToSave: MapLayerConfiguration;
  saving = false;

  protected readonly MapLayerType = MapLayerType;
  private tenantConfigurationSubscription: Subscription;

  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly settingsService: SettingsService,
    private readonly dialog: MatDialog,
    private readonly toast: ToastService
  ) {}

  ngOnInit(): void {
    this.tenantConfigurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(tenantConfiguration => {
      if (!tenantConfiguration) {
        return;
      }
      this.configuration = tenantConfiguration;
      this.reloadConfigurations();
    });
  }

  async loadMapLayerConfigurations() {
    return this.configurationService
      .getAllMapLayers()
      .then(layers => {
        this.mapLayers = layers;
        this.mapLayers.forEach(value => {
          value.useCredentials = !!value.username && !!value.password;
          value.opacity = value.opacity ?? 1.0;
        });
        return this.mapLayers.length > 0;
      })
      .catch(error => {
        const msg = 'Error while loading map layer configurations';
        console.error(msg, error);
        this.toast.long(msg);
        throw Error(error);
      });
  }

  ngOnDestroy() {
    this.tenantConfigurationSubscription?.unsubscribe();
  }

  async saveMapLayers() {
    this.saving = true;
    return this.configurationService.updateMapLayers(this.mapLayers).finally(() => {
      this.saving = false;
      this.editedMapLayer = null;
      this.mapLayerToSave = null;
      this.mapLayerIndex = -1;

      // make sure that current basemap still exists
      const currentBaseMap = this.settingsService.getStringValue(SettingsService.BASE_MAP_LAYER_KEY);
      const currentCustomBaseMapId = this.settingsService.getStringValue(SettingsService.CUSTOM_BASE_MAP_KEY);
      if (currentBaseMap === BaseMapType.CUSTOM) {
        const customBaseMapLayer = this.mapLayers.find(l => {
          return l.useAsBaseMap && l.name === currentCustomBaseMapId;
        });
        if (!customBaseMapLayer) {
          // reset to Winter style
          this.settingsService.setStringValue(SettingsService.BASE_MAP_LAYER_KEY, BaseMapType.WINTER);
        }
      }
    });
  }

  editLayer(index: number) {
    this.editing.emit(true);
    this.mapLayerIndex = index;
    this.editedMapLayer = Object.assign({}, this.mapLayers[index]);
  }

  editNewLayer() {
    this.mapLayerIndex = -1;
    const mapLayer = new MapLayerConfiguration();
    mapLayer.type = MapLayerType.RASTER;
    mapLayer.source = MapLayerSource.TileUrl;
    mapLayer.order = this.mapLayers.length > 0 ? this.mapLayers[this.mapLayers.length - 1].order + 1 : 1;
    mapLayer.enabled = true;
    mapLayer.opacity = 1.0;
    this.editing.emit(true);
    this.editedMapLayer = mapLayer;
  }

  backToList() {
    let confirmationPromise = Promise.resolve(true);
    if (this.mapLayerToSave) {
      confirmationPromise = firstValueFrom(
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Confirmation',
              text: 'Current layer modification is not saved. Do you want to discard changes?',
            },
          })
          .afterClosed()
      );
    }
    confirmationPromise.then(async result => {
      if (!result) {
        return null;
      }
      this.editing.emit(false);
      this.editedMapLayer = null;
      this.mapLayerToSave = null;
      this.reloadConfigurations();
    });
  }

  save() {
    this.saving = true;
    if (this.mapLayerIndex === -1) {
      this.mapLayers.push(this.mapLayerToSave);
    } else {
      this.mapLayers.splice(this.mapLayerIndex, 1, this.mapLayerToSave);
    }
    this.saveMapLayers().then(savedLayers => {
      this.mapLayers = savedLayers;
      this.editing.emit(false);
      this.editedMapLayer = null;
      this.mapLayerToSave = null;
      this.mapLayerIndex = -1;
    });
  }

  onLayerEditorChange(event: { valid: boolean; layer: MapLayerConfiguration }) {
    this.editingStateValid = event.valid;
    this.mapLayerToSave = event.layer;

    // debug
    // console.log(`valid: ${event.valid}`);
    // console.log(event.layer);
  }

  async deleteLayer(layer: MapLayerConfiguration, index: number) {
    let confirmationPromise = Promise.resolve(true);
    if (layer.publicEnabled === true) {
      // it can be null or undefined, so check it is really set to true
      confirmationPromise = firstValueFrom(
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Delete Layer Confirmation',
              text: 'The layer is referenced in the public portal. Do you really want to delete it?',
            },
          })
          .afterClosed()
      );
    }
    confirmationPromise.then(async result => {
      if (!result) {
        return null;
      }
      this.mapLayers.splice(index, 1);
      this.mapLayers = await this.saveMapLayers();
      this.toast.long('Map layer has been deleted');
    });
  }

  async moveLayerDown(index: number) {
    if (index < 0 || index >= this.mapLayers.length - 1) {
      return Promise.resolve();
    }
    const firstItem = this.mapLayers[index];
    const secondItem = this.mapLayers[index + 1];
    const secondItemOrder = secondItem.order;

    secondItem.order = firstItem.order;
    firstItem.order = secondItemOrder;

    return this.saveMapLayers().then(savedLayers => (this.mapLayers = savedLayers));
  }

  private reloadConfigurations() {
    this.loadMapLayerConfigurations().then(_ => {
    });
  }
}
